<script setup>
import { reactive } from 'vue';
import UiAvatar from './UiAvatar.vue';
import UiEmptyState from './UiEmptyState.vue';
import { timeAgo } from '@/utils/timeAgo';
import { formatDateTime } from '@/utils/format';

// A feed of posts with collapsible comments (announcements). The parent
// sends the comment request and reloads:
//   <UiPostFeed :posts="posts" :sending="busy" @comment="(post, text) => …"
//     :can-delete-comment="(post, c) => …" @delete-comment="(post, c) => …" />
// Posts: { id, author, date, text, comments: [{ id, name, text }] }.
defineProps({
  posts: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  sending: { type: Boolean, default: false },
  emptyTitle: { type: String, default: 'No posts yet' },
  emptyDescription: { type: String, default: '' },
  // (post, comment) => whether to offer Delete on that comment.
  canDeleteComment: { type: Function, default: null },
});
const emit = defineEmits(['comment', 'delete-comment']);

const expanded = reactive(new Set());
const drafts = reactive({});

function toggle(post) {
  if (expanded.has(post.id)) expanded.delete(post.id);
  else expanded.add(post.id);
}

function send(post) {
  const text = (drafts[post.id] || '').trim();
  if (!text) return;
  emit('comment', post, text);
  drafts[post.id] = '';
}
</script>

<template>
  <div v-if="loading && !posts.length" class="feed">
    <v-skeleton-loader v-for="n in 3" :key="n" type="list-item-avatar-three-line" class="post" />
  </div>

  <UiEmptyState v-else-if="!posts.length" icon="mdi-bullhorn-outline" :title="emptyTitle" :description="emptyDescription" />

  <div v-else class="feed">
    <article v-for="post in posts" :key="post.id" class="post">
      <header class="post__header">
        <UiAvatar :name="post.author" :size="36" />
        <div class="post__meta">
          <span class="post__author">{{ post.author }}</span>
          <time class="post__time" :title="formatDateTime(post.date)">{{ timeAgo(post.date) }}</time>
        </div>
      </header>

      <p class="post__text">{{ post.text }}</p>

      <button type="button" class="post__toggle" :aria-expanded="expanded.has(post.id)" @click="toggle(post)">
        <v-icon size="16">mdi-comment-outline</v-icon>
        {{ post.comments.length }} {{ post.comments.length === 1 ? 'comment' : 'comments' }}
      </button>

      <div v-if="expanded.has(post.id)" class="post__comments">
        <div v-for="(c, i) in post.comments" :key="c.id ?? i" class="comment">
          <UiAvatar :name="c.name" :size="24" />
          <div class="comment__body">
            <span class="comment__author">{{ c.name }}</span>
            <span>{{ c.text }}</span>
          </div>
          <v-btn
            v-if="canDeleteComment?.(post, c)"
            icon="mdi-trash-can-outline"
            size="x-small"
            variant="text"
            class="comment__delete"
            :aria-label="`Delete ${c.name}'s comment`"
            @click="emit('delete-comment', post, c)"
          />
        </div>

        <form class="comment-form" @submit.prevent="send(post)">
          <v-text-field v-model="drafts[post.id]" placeholder="Write a comment…" density="compact" hide-details />
          <v-btn
            type="submit"
            icon="mdi-send"
            size="small"
            variant="text"
            :disabled="!drafts[post.id]?.trim()"
            :loading="sending"
            aria-label="Send comment"
          />
        </form>
      </div>
    </article>
  </div>
</template>

<style scoped>
.feed {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 720px;
}
.post {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--card);
}
.post__header {
  display: flex;
  align-items: center;
  gap: 12px;
}
.post__meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.post__author {
  font-size: 14px;
  font-weight: 500;
}
.post__time {
  font-size: 12px;
  color: var(--muted-foreground);
}
.post__text {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-line;
}
.post__toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  padding: 4px 8px;
  margin-left: -8px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--muted-foreground);
  font: inherit;
  font-size: 13px;
  cursor: pointer;
}
.post__toggle:hover {
  background-color: var(--accent);
  color: var(--foreground);
}
.post__comments {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid var(--border);
}
.comment {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.comment__delete {
  margin-left: auto;
  color: var(--muted-foreground);
}
.comment__body {
  display: flex;
  flex-direction: column;
  padding: 6px 10px;
  border-radius: 8px;
  background-color: var(--accent);
  font-size: 13px;
}
.comment__author {
  font-weight: 500;
}
.comment-form {
  display: flex;
  align-items: center;
  gap: 4px;
}
</style>
